<template>
  <transition name="loader-fade">
    <section
      class="loader-backdrop"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <section class="loading-box">
        <section class="rocket-scene" aria-hidden="true">
          <span class="orbit-ring"></span>
          <span class="star star-one"></span>
          <span class="star star-two"></span>
          <span class="star star-three"></span>
          <span class="exhaust exhaust-one"></span>
          <span class="exhaust exhaust-two"></span>
          <span class="exhaust exhaust-three"></span>
          <img :src="rocket" class="rocket-img" alt="" />
        </section>
        <p class="loader-text">{{ text }}</p>
      </section>
    </section>
  </transition>
</template>

<script>
import rocket from "../assets/rocket4.png";

export default {
  props: {
    text: {
      required: false,
      type: String,
      default: "Please wait...",
    },
  },
  data() {
    return {
      rocket,
    };
  },
};
</script>

<style scoped>
.loader-fade-enter,
.loader-fade-leave-to {
  opacity: 0;
}

.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: opacity 0.24s ease;
}

.loader-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.36);
  backdrop-filter: blur(4px);
}

.loading-box {
  min-width: min(320px, 90vw);
  border: 1px solid rgba(49, 82, 245, 0.12);
  border-radius: 18px;
  padding: 1.5rem 1.75rem 1.35rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.rocket-scene {
  position: relative;
  width: 132px;
  height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orbit-ring {
  position: absolute;
  inset: 9px;
  border: 2px solid #3152f5;
  border-top-color: #e4ff26;
  border-right-color: #e4ff26;
  border-radius: 999px;
  animation: orbit-spin 3.8s linear infinite;
}

.orbit-ring::before {
  position: absolute;
  inset: 16px 10px;
  border: 1px dashed #e4ff26;
  border-radius: inherit;
  content: "";
  transform: rotate(-22deg);
}

.orbit-ring::after {
  position: absolute;
  top: 10px;
  right: 20px;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #e4ff26;
  box-shadow: -68px 42px 0 -2px #3152f5;
  content: "";
}

.rocket-img {
  position: relative;
  z-index: 2;
  width: auto;
  height: 92px;
  object-fit: contain;
  transform-origin: 50% 50%;
  filter: drop-shadow(0 16px 12px rgba(15, 23, 42, 0.18));
  animation: rocket-float 1.15s ease-in-out infinite;
}

.star,
.exhaust {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
}

.star {
  width: 7px;
  height: 7px;
  background: rgba(49, 82, 245, 0.4);
  animation: star-pulse 1.7s ease-in-out infinite;
}

.star-one {
  top: 25px;
  right: 28px;
}

.star-two {
  top: 61px;
  left: 19px;
  width: 5px;
  height: 5px;
  background: #e4ff26;
  animation-delay: 0.24s;
}

.star-three {
  right: 22px;
  bottom: 38px;
  width: 4px;
  height: 4px;
  animation-delay: 0.48s;
}

.exhaust {
  z-index: 1;
  left: 34px;
  bottom: 28px;
  width: var(--size);
  height: var(--size);
  background: var(--color);
  box-shadow: 0 0 12px var(--glow);
  animation: exhaust-pop 1.15s ease-out infinite;
  animation-delay: var(--delay);
}

.exhaust-one {
  --delay: 0s;
  --size: 9px;
  --x: -6px;
  --color: rgba(148, 163, 184, 0.72);
  --glow: rgba(100, 116, 139, 0.28);
}

.exhaust-two {
  --delay: 0.06s;
  --size: 12px;
  --x: 5px;
  --color: rgba(148, 163, 184, 0.78);
  --glow: rgba(100, 116, 139, 0.3);
  bottom: 24px;
}

.exhaust-three {
  --delay: 0.12s;
  --size: 8px;
  --x: 15px;
  --color: rgba(148, 163, 184, 0.66);
  --glow: rgba(100, 116, 139, 0.24);
}

.loader-text {
  margin: 0.8rem 0 0;
  color: #0f172a;
  font-size: 0.96rem;
  font-weight: 800;
  text-align: center;
}

@keyframes rocket-float {
  0%,
  100% {
    transform: translateY(4px) rotate(45deg);
  }

  50% {
    transform: translateY(-6px) rotate(45deg);
  }
}

@keyframes orbit-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes exhaust-pop {
  0% {
    opacity: 0.92;
    transform: translate(var(--x), -2px) scale(0.68);
  }

  38% {
    opacity: 0.38;
    transform: translate(calc(var(--x) - 7px), 14px) scale(1.15);
  }

  58% {
    opacity: 0;
    transform: translate(calc(var(--x) - 11px), 22px) scale(1.42);
  }

  78% {
    opacity: 0;
    transform: translate(var(--x), 2px) scale(0.7);
  }

  100% {
    opacity: 0.88;
    transform: translate(var(--x), -2px) scale(0.68);
  }
}

@keyframes star-pulse {
  0%,
  100% {
    opacity: 0.28;
    transform: scale(0.75);
  }

  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@media (prefers-reduced-motion: reduce) {
  .orbit-ring,
  .rocket-img,
  .exhaust,
  .star {
    animation: none;
  }
}
</style>
